/**
 * The Finish Line at the top of the job page (#1714, the v6 prototype:
 * https://claude.ai/artifact/E1peGaPNnPfBGt7EFagSfb).
 *
 * One white sheet under the job header:
 *   1. NEXT: the open office move that unblocks the most, its "doesn't add
 *      up" line, THE one filled button on the page, and the chain it unblocks.
 *      With no next move: who the job is waiting on, or "every promise kept".
 *   2. The METER: kept / on us / blocked / waiting, the per-phase tallies,
 *      and "See all" (the stacked colour bar shows only once it's open).
 *   3. The full LIST, grouped by phase, when opened.
 *
 * The engine (`GET /api/jobs/:id/finish-line`) decides every state. This
 * component only says it, and its buttons call what already exists: the
 * check-up's one-tap fix (`useCheckupFix`) or a link.
 *
 * Teach it once (#1738): a promise a taught rule made or shaped carries a
 * small "Rule" tag and who taught it; its action ("Mark done", "Mark sent by
 * portal") opens a note and marks it kept on this job, with Undo on the kept
 * row. The list ends with a quiet "+ Add a rule for every job like this", and a
 * row the office just worked around offers "Make this a rule".
 */
import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { finishLineKey, type FinishLine, type JobPromise, type PromiseAction } from "@/lib/finish-line";
import { useCheckupFix } from "@/lib/use-job-checkup";
import { ruleSuggestion, taughtWords, useRuleCheck } from "@/lib/promise-rules";
import type { RuleKind } from "@/lib/finish-line";
import { MarkRuleDialog } from "./mark-rule-dialog";
import { SentAnotherWayDialog } from "@/components/sent-another-way-dialog";
import { TeachRuleDialog, type TeachPreset } from "./teach-rule-dialog";
import { AlsoOpenList } from "./also-open";
import {
    STATE_GLYPH,
    ageWords,
    blockedByTitle,
    groupByPhase,
    linkTarget,
    meterWidths,
    phaseTallies,
    stateLabel,
    truthSentences,
    waitingHeadline,
    type LinkTarget,
} from "./finishLineModel";

export type FinishLineSection = Extract<LinkTarget, { kind: "section" }>["section"];

const STATE_INK: Record<JobPromise["state"], string> = {
    kept: "text-emerald-700 dark:text-emerald-400",
    open: "text-amber-700 dark:text-amber-400",
    missed: "text-amber-700 dark:text-amber-400",
    blocked: "text-red-700 dark:text-red-400",
    waiting: "text-blue-700 dark:text-blue-400",
    not_applicable: "text-muted-foreground",
    later: "text-muted-foreground",
};

const runnable = (a: PromiseAction | undefined): a is PromiseAction =>
    !!a && (!!a.fix || !!a.href || (a.kind === "rule_check" && !!a.ruleId) || a.kind === "sent_another_way");

export function FinishLinePanel({
    finishLine: fl,
    preVisit,
    onSection,
    now,
    initiallyOpen = false,
    alsoOpen = false,
    checkup,
}: {
    finishLine: FinishLine;
    /** Nobody has been to the site yet: "Pre-flight". */
    preVisit: boolean;
    /** A link back to this job's own page opens that part of it. */
    onSection: (section: FinishLineSection) => void;
    /** For tests and proofs; defaults to the clock. */
    now?: Date;
    /** Start with the full list open (the render proof). */
    initiallyOpen?: boolean;
    /** Job page v2: up to 3 more open promises under Next. */
    alsoOpen?: boolean;
    /** Job page v2: the Help Desk check-up strip, under Next. */
    checkup?: ReactNode;
}) {
    const [open, setOpen] = useState(initiallyOpen);
    const navigate = useNavigate();
    const qc = useQueryClient();
    const fix = useCheckupFix(fl.jobId);
    const ruleCheck = useRuleCheck(fl.jobId);
    const [marking, setMarking] = useState<JobPromise | null>(null);
    const [otherWay, setOtherWay] = useState(false);
    const [teach, setTeach] = useState<TeachPreset | null>(null);
    const clock = now ?? new Date();

    const run = (a: PromiseAction) => {
        if (a.kind === "sent_another_way") {
            setOtherWay(true);
            return;
        }
        if (a.kind === "rule_check") {
            setMarking(fl.promises.find((p) => p.action?.ruleId === a.ruleId) ?? null);
            return;
        }
        if (a.fix) {
            fix.mutate(a.fix, {
                onSuccess: (res) => (res.ok ? toast.success(res.message) : toast.error(res.message)),
                onError: (e) => toast.error(`That didn't work: ${e.message}`),
                onSettled: () => void qc.invalidateQueries({ queryKey: finishLineKey(fl.jobId) }),
            });
            return;
        }
        if (!a.href) return;
        const t = linkTarget(a.href, fl.jobId);
        if (t.kind === "section") onSection(t.section);
        else navigate(t.to);
    };

    const next = fl.next ? (fl.promises.find((p) => p.id === fl.next!.promiseId) ?? null) : null;

    const markKept = (note: string | null) => {
        const ruleId = marking?.action?.ruleId;
        if (!ruleId) return;
        ruleCheck.mark.mutate(
            { ruleId, note },
            {
                onSuccess: () => {
                    toast.success(`Marked: ${marking!.title}.`);
                    setMarking(null);
                },
                onError: (e) => toast.error(`That didn't save: ${e.message}`),
            },
        );
    };
    const undo = (p: JobPromise) => {
        if (!p.rule) return;
        ruleCheck.undo.mutate(p.rule.id, {
            onSuccess: () => toast.success(`Undone: ${p.title} is open again.`),
            onError: (e) => toast.error(`That didn't undo: ${e.message}`),
        });
    };

    return (
        <section
            className="grid gap-3 rounded-xl border bg-card px-4 py-6 shadow-sm sm:px-7"
            aria-label={preVisit ? "Pre-flight" : "Finish Line"}
            data-testid="finish-line"
        >
            {next && fl.next ? (
                <NextMove
                    fl={fl}
                    promise={next}
                    unblocks={fl.next.unblocks.length}
                    path={fl.next.path}
                    preVisit={preVisit}
                    now={clock}
                    busy={fix.isPending || ruleCheck.mark.isPending}
                    onRun={run}
                />
            ) : (
                <NoNextMove fl={fl} preVisit={preVisit} now={clock} />
            )}
            {alsoOpen && (
                <AlsoOpenList
                    fl={fl}
                    nextId={next?.id ?? null}
                    now={clock}
                    busy={fix.isPending || ruleCheck.mark.isPending}
                    onRun={run}
                    runnable={runnable}
                />
            )}
            {checkup}
            <Meter fl={fl} preVisit={preVisit} open={open} onToggle={() => setOpen((o) => !o)} />
            {open && (
                <PromiseList
                    fl={fl}
                    nextId={next?.id ?? null}
                    now={clock}
                    onRun={run}
                    onSection={onSection}
                    onTeach={(kind) => setTeach(kind ? { kind } : {})}
                    onUndo={undo}
                />
            )}
            {marking?.action && (
                <MarkRuleDialog
                    open
                    onClose={() => setMarking(null)}
                    title={marking.title}
                    actionLabel={marking.action.label}
                    saving={ruleCheck.mark.isPending}
                    onSave={markKept}
                />
            )}
            <SentAnotherWayDialog jobId={fl.jobId} open={otherWay} onClose={() => setOtherWay(false)} />
            {teach && <TeachRuleDialog open onClose={() => setTeach(null)} jobId={fl.jobId} preset={teach} />}
        </section>
    );
}

// ── 1. Next ─────────────────────────────────────────────────────────────────

function NextMove({
    fl,
    promise: p,
    unblocks,
    path,
    preVisit,
    now,
    busy,
    onRun,
}: {
    fl: FinishLine;
    promise: JobPromise;
    unblocks: number;
    path: string[];
    preVisit: boolean;
    now: Date;
    busy: boolean;
    onRun: (a: PromiseAction) => void;
}) {
    const age = ageWords(p.since, now);
    const truths = truthSentences(fl, p);
    return (
        <div className="grid gap-3" data-testid="finish-line-next">
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[13px] text-muted-foreground">
                <b className="font-bold text-amber-700 dark:text-amber-400">
                    {preVisit ? "Pre-flight · next" : "Next"}
                </b>
                {unblocks > 0 && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 font-bold text-primary">
                        unblocks {unblocks}
                    </span>
                )}
                {age && <span>Owed {age}</span>}
                {p.dueBy && <span>Due {formatDay(p.dueBy)}</span>}
            </div>
            <h2 className="m-0 max-w-[60ch] text-[22px] font-bold leading-snug tracking-tight text-foreground">
                {p.title}
            </h2>
            <p className="m-0 max-w-[70ch] text-[15px] text-foreground/80">{p.detail}</p>
            {truths.length > 0 && (
                <p
                    className="m-0 max-w-[72ch] text-[15px] text-red-800 dark:text-red-300"
                    data-testid="finish-line-truth"
                >
                    <b className="font-bold">Doesn&apos;t add up.</b> {truths.join(" ")}
                </p>
            )}
            {runnable(p.action) && (
                <div className="flex flex-wrap items-center gap-2.5">
                    <Button
                        className="h-auto px-[18px] py-3 text-[15px] font-semibold"
                        disabled={busy}
                        onClick={() => onRun(p.action!)}
                    >
                        {p.action.label}
                    </Button>
                </div>
            )}
            {path.length > 0 && (
                <p className="m-0 text-[13px] text-muted-foreground" data-testid="finish-line-path">
                    Why this first: it unblocks{" "}
                    {path.map((step, i) => (
                        <span key={`${step}-${i}`}>
                            {i > 0 && " → "}
                            <span className="font-semibold text-foreground/80">{step}</span>
                        </span>
                    ))}
                </p>
            )}
        </div>
    );
}

function NoNextMove({ fl, preVisit, now }: { fl: FinishLine; preVisit: boolean; now: Date }) {
    const w = waitingHeadline(fl);
    if (!w) {
        return (
            <div className="grid gap-3" data-testid="finish-line-done">
                <div className="text-[13px]">
                    <b className="font-bold text-emerald-700 dark:text-emerald-400">
                        {preVisit ? "Ready for the tech" : "Done"}
                    </b>
                </div>
                <h2 className="m-0 text-[22px] font-bold leading-snug tracking-tight text-foreground">
                    {(fl.counts.later ?? 0) > 0
                        ? "Everything before the visit is ready."
                        : "Every promise on this job is kept."}
                </h2>
                {(fl.counts.later ?? 0) > 0 && (
                    <p className="m-0 text-[15px] text-foreground/80" data-testid="finish-line-later-note">
                        {fl.counts.later} more start after the visit.
                    </p>
                )}
            </div>
        );
    }
    const age = ageWords(w.promise.since, now);
    const ours = w.who === "us";
    return (
        <div className="grid gap-3" data-testid="finish-line-waiting">
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[13px] text-muted-foreground">
                <b
                    className={cn(
                        "font-bold",
                        ours ? "text-amber-700 dark:text-amber-400" : "text-blue-700 dark:text-blue-400",
                    )}
                >
                    {ours ? "On us" : `Waiting on ${w.who}`}
                </b>
                {age && <span>Owed {age}</span>}
            </div>
            <h2 className="m-0 max-w-[60ch] text-[22px] font-bold leading-snug tracking-tight text-foreground">
                {w.promise.title}
            </h2>
            <p className="m-0 max-w-[70ch] text-[15px] text-foreground/80">
                {ours ? w.promise.detail : `Nothing on us. ${w.promise.detail}`}
            </p>
        </div>
    );
}

// ── 2. Meter ────────────────────────────────────────────────────────────────

function Meter({
    fl,
    preVisit,
    open,
    onToggle,
}: {
    fl: FinishLine;
    preVisit: boolean;
    open: boolean;
    onToggle: () => void;
}) {
    const c = fl.counts;
    const w = meterWidths(fl);
    return (
        <button
            type="button"
            className="grid w-full gap-2 border-t pt-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            aria-expanded={open}
            aria-controls="finish-line-list"
            onClick={onToggle}
            data-testid="finish-line-meter"
        >
            <span className="flex flex-wrap items-baseline gap-x-[18px] gap-y-1 text-sm text-foreground/80">
                <span className="font-bold text-foreground">{preVisit ? "Pre-flight" : "Finish Line"}</span>
                <span data-testid="meter-kept">
                    <b className="font-bold">{c.kept}</b> of {c.total} kept
                </span>
                {c.onUs > 0 && (
                    <span className="text-amber-700 dark:text-amber-400" data-testid="meter-onus">
                        <b className="font-bold">{c.onUs}</b> on us
                    </span>
                )}
                {c.blocked > 0 && (
                    <span className="text-red-700 dark:text-red-400" data-testid="meter-blocked">
                        <b className="font-bold">{c.blocked}</b> blocked
                    </span>
                )}
                {c.waiting > 0 && (
                    <span className="text-blue-700 dark:text-blue-400" data-testid="meter-waiting">
                        <b className="font-bold">{c.waiting}</b> waiting
                    </span>
                )}
                {(c.later ?? 0) > 0 && (
                    <span className="text-muted-foreground" data-testid="meter-later">
                        <b className="font-bold">{c.later}</b> after the visit
                    </span>
                )}
                <span className="ml-auto font-semibold text-primary">{open ? "Hide ▴" : "See all ▾"}</span>
            </span>
            {/* Job page v3 (10/9): the colored bar lives inside "See all",
                not at the top; closed, the counts say it in words. */}
            {open && (
                <span
                    className="flex h-1.5 overflow-hidden rounded-full bg-muted"
                    aria-hidden
                    data-testid="finish-line-bar"
                >
                    <i className="block h-full bg-emerald-700" style={{ width: `${w.kept}%` }} />
                    <i className="block h-full bg-amber-600" style={{ width: `${w.onUs}%` }} />
                    <i className="block h-full bg-red-600" style={{ width: `${w.blocked}%` }} />
                    <i className="block h-full bg-blue-500" style={{ width: `${w.waiting}%` }} />
                </span>
            )}
            <span className="flex flex-wrap gap-x-[18px] gap-y-1 text-[13px] text-muted-foreground">
                {phaseTallies(fl).map((t) => (
                    <span key={t.phase} data-testid={`meter-phase-${t.phase}`}>
                        {t.label}{" "}
                        <b className="font-semibold text-foreground/80">
                            {t.kept}/{t.total}
                        </b>
                    </span>
                ))}
            </span>
        </button>
    );
}

// ── 3. The list ─────────────────────────────────────────────────────────────

function PromiseList({
    fl,
    nextId,
    now,
    onRun,
    onSection,
    onTeach,
    onUndo,
}: {
    fl: FinishLine;
    nextId: string | null;
    now: Date;
    onRun: (a: PromiseAction) => void;
    onSection: (section: FinishLineSection) => void;
    onTeach: (kind?: RuleKind) => void;
    onUndo: (p: JobPromise) => void;
}) {
    return (
        <div id="finish-line-list" className="mt-1 grid gap-1" data-testid="finish-line-list">
            {groupByPhase(fl).map((g) => (
                <div key={g.phase} data-testid={`finish-line-group-${g.phase}`}>
                    <h3 className="mb-0.5 mt-3 text-[13px] font-bold uppercase tracking-wider text-muted-foreground">
                        {g.label}
                    </h3>
                    {g.promises.map((p) => (
                        <PromiseRow
                            key={p.id}
                            fl={fl}
                            p={p}
                            isNext={p.id === nextId}
                            now={now}
                            onRun={onRun}
                            onSection={onSection}
                            onTeach={onTeach}
                            onUndo={onUndo}
                        />
                    ))}
                </div>
            ))}
            <div className="border-t pt-3">
                <button type="button" className={QUIET_LINK} onClick={() => onTeach()} data-testid="finish-line-teach">
                    + Add a rule for every job like this
                </button>
            </div>
        </div>
    );
}

const QUIET_LINK =
    "rounded-sm text-[13px] font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function PromiseRow({
    fl,
    p,
    isNext,
    now,
    onRun,
    onSection,
    onTeach,
    onUndo,
}: {
    fl: FinishLine;
    p: JobPromise;
    isNext: boolean;
    now: Date;
    onRun: (a: PromiseAction) => void;
    onSection: (section: FinishLineSection) => void;
    onTeach: (kind?: RuleKind) => void;
    onUndo: (p: JobPromise) => void;
}) {
    const suggest = ruleSuggestion(p);
    const undoable = p.state === "kept" && !!p.rule?.check;
    const truths = p.state === "kept" ? [] : truthSentences(fl, p);
    const blocker = blockedByTitle(fl, p);
    const ink = STATE_INK[p.state];
    const canAct = (p.state === "open" || p.state === "missed") && runnable(p.action) && !isNext;
    return (
        <div
            className={cn(
                "grid grid-cols-[20px_minmax(0,1fr)_104px] items-baseline gap-x-3 gap-y-0.5 border-t py-2 text-sm md:grid-cols-[20px_minmax(0,1.3fr)_minmax(0,1fr)_150px]",
                isNext && "-mx-2 rounded-md bg-amber-50 px-2 dark:bg-amber-500/10",
            )}
            data-testid={`promise-${p.id}`}
            data-state={p.state}
        >
            <span className={cn("text-center font-extrabold", ink)} aria-hidden>
                {STATE_GLYPH[p.state]}
            </span>
            <span className="min-w-0 font-semibold text-foreground">
                {p.title}
                {p.rule && (
                    <span
                        className="ml-2 inline-block rounded-sm border border-border px-1.5 align-[1px] text-[13px] font-semibold leading-tight text-muted-foreground"
                        data-testid={`rule-tag-${p.id}`}
                    >
                        Rule
                    </span>
                )}
                <small className="block text-[13px] font-normal text-muted-foreground">{p.detail}</small>
                {p.rule && (
                    <small
                        className="block text-[13px] font-normal text-muted-foreground"
                        data-testid={`rule-taught-${p.id}`}
                    >
                        {taughtWords(p.rule)}
                    </small>
                )}
                {truths.map((s) => (
                    <small key={s} className="block text-[13px] font-normal text-red-800 dark:text-red-300">
                        Doesn&apos;t add up: {s}
                    </small>
                ))}
                {blocker && (
                    <small className="block text-[13px] font-normal text-red-700 dark:text-red-400">
                        Blocked by: {blocker}
                    </small>
                )}
            </span>
            <span className={cn("text-right text-[13px] font-semibold md:col-start-4 md:row-start-1", ink)}>
                {stateLabel(p, now)}
                {canAct && (
                    <>
                        <br />
                        <button
                            type="button"
                            className="rounded-sm text-[13px] font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() => onRun(p.action!)}
                        >
                            {p.action!.label} →
                        </button>
                    </>
                )}
                {isNext && (
                    <>
                        <br />
                        <span className="text-[13px] text-amber-700 dark:text-amber-400">Next ↑</span>
                    </>
                )}
                {undoable && (
                    <>
                        <br />
                        <button type="button" className={QUIET_LINK} onClick={() => onUndo(p)}>
                            Undo
                        </button>
                    </>
                )}
                {suggest && (
                    <>
                        <br />
                        <button type="button" className={QUIET_LINK} onClick={() => onTeach(suggest)}>
                            Make this a rule
                        </button>
                    </>
                )}
            </span>
            <span className="col-span-2 col-start-2 text-[13px] text-muted-foreground md:col-span-1 md:col-start-3 md:row-start-1">
                {p.proof && <ProofLabel fl={fl} proof={p.proof} onSection={onSection} />}
            </span>
        </div>
    );
}

function ProofLabel({
    fl,
    proof,
    onSection,
}: {
    fl: FinishLine;
    proof: NonNullable<JobPromise["proof"]>;
    onSection: (section: FinishLineSection) => void;
}) {
    if (!proof.href) return <>{proof.label}</>;
    const t = linkTarget(proof.href, fl.jobId);
    if (t.kind === "section") {
        // This job's own page: open that part of it rather than reload.
        return (
            <button
                type="button"
                className="rounded-sm text-left text-foreground/80 underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onSection(t.section)}
            >
                {proof.label}
            </button>
        );
    }
    return (
        <Link to={t.to} className="text-foreground/80 underline-offset-2 hover:text-primary hover:underline">
            {proof.label}
        </Link>
    );
}

function formatDay(ymd: string): string {
    const d = new Date(`${ymd}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return ymd;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}
